<template>
	<div class="journeys-skew-banner">
		<div class="container">
			<div class="row">
				<div class="col journeys-skew-banner__content text-center">
					<h1 class="h2">{{ microcopy?.heading }}</h1>
					<p>{{ microcopy?.summary }}</p>
				</div>
			</div>
		</div>
	</div>
	<div class="container pull-up">
		<form
			class="w-100"
			@submit.prevent
		>
			<div class="rounded-container">
				<div class="row flex-column">
					<div class="col d-flex flex-column flex-md-row rounded-container__btn-col rounded-container__btn-col-x2">
						<fieldset>
							<legend class="visually-hidden"><span>Please select an option</span></legend>
							<RadioInputComponent
								v-for="option in options"
								:key="option"
								:options="option"
								name="first-step"
								:selected-option="getFirstStep(option)"
								@selected="(payload) => {
									setFirstStep(payload);
								}"
							/>
						</fieldset>
					</div>
					<div v-if="selected.firstStep.selected || selected.remortgage.firstStep.selected">
						<div
							v-if="planningToRemortgage"
							id="remortgage-container"
						>
							<div class="col">
								<div class="row">
									<div class="col-12">
										<h2 class="h4 mb-4 mt-custom-1">{{ microcopy?.remortgageStageLabel }}</h2>
									</div>
								</div>
							</div>
							<div class="row">
								<div class="col d-flex flex-column flex-md-row rounded-container__btn-col rounded-container__btn-col-x2--small">
									<fieldset>
										<legend class="visually-hidden"><span>Please select an option</span></legend>
										<RadioInputComponent
											v-for="option in remortgageOptions"
											:key="option"
											:options="option"
											name="second-step"
											:selected-option="getSecondStep(option)"
											@selected="(payload) => {
												setSecondStep(payload);
											}"
										/>
									</fieldset>
								</div>
							</div>
						</div>
						<div
							v-else
							class="col"
							id="property-stage-container"
						>
							<div class="row">
								<div class="col-12">
									<h2 class="h4 mb-4 mt-custom-1">{{ microcopy?.propertySearchStageLabel }}</h2>
								</div>
							</div>
							<div class="row">
								<div class="col d-flex flex-column flex-md-row rounded-container__btn-col rounded-container__btn-col-x2--small">
									<fieldset>
										<legend class="visually-hidden"><span>Please select an option</span></legend>
										<RadioInputComponent
											v-for="option in newMortgageOptions"
											:key="option"
											:options="option"
											name="second-step"
											:selected-option="selected.secondStep"
											@selected="(payload) => {
												postPropertySearchStage(payload);
												selected.secondStep = payload;
												if (payload.selected) {
													scrollToNextQuestion('how-wesleyan-can-help-container');
												}
											}"
										/>
									</fieldset>
								</div>
							</div>
							<div
								v-if="thirdStageToBeShown"
								class="col"
								id="how-wesleyan-can-help-container"
							>
								<div class="row">
									<div class="col-12">
										<h2 class="h4 mb-4 mt-custom-1">{{ microcopy?.howCanWeHelpLabel }}</h2>
									</div>
								</div>
								<div class="row">
									<div class="col d-flex flex-column flex-md-row rounded-container__btn-col rounded-container__btn-col-x2--small">
										<fieldset>
											<legend class="visually-hidden"><span>Please select an option</span></legend>
											<RadioInputComponent
												v-for="option in thirdStageOptions"
												:key="option"
												:options="option"
												name="third-step"
												:selected-option="selected.thirdStep"
												@selected="(payload) => {
													postHowWesleyanCanHelp(payload);
													selected.thirdStep = payload;

													if (payload.selected) {
														scrollToNextQuestion('continue-button');
													}
												}"
											/>
										</fieldset>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="row btm-btn-group__contained">
					<div class="col text-center d-flex flex-column align-items-start">
						<GenericButton
							v-if="continueButtonToBeShown"
							id="continue-button"
							:label-text="progressionText"
							:button-disabled="!allButtonsValid"
							@button-click="handleClick"
						/>
					</div>
				</div>
			</div>
		</form>
	</div>
	<div class="container">
		<div class="row">
			<div class="col">
				<div
					class="rounded-container__secondary--sand"
					v-html="microcopy?.mortgageComplianceMessage"
				>
				</div>

			</div>
		</div>
	</div>
</template>

<script>
import RadioInputComponent from '@/shared/components/RadioInputComponent.vue';
import GenericButton from '@/shared/components/GenericButton.vue';

import router from '../router/index.js';

import { mapStores, mapActions, mapState } from 'pinia';
import { useMortgageStore } from '../stores/mortgageStore.js';

export default {
	components: {
		RadioInputComponent,
		GenericButton,
	},
	data() {
		return {
			microcopy: '',
			remortgageOptions: {
				toLiveIn: {
					text: 'To live in',
					type: 'liveIn'
				},
				toRentOut: {
					text: 'To rent out',
					type: 'rentOut'
				},
			},
			newMortgageOptions: {
				researching: {
					text: 'I\'m just researching',
					type: 'researching'
				},
				viewingProperties: {
					text: 'I\'m viewing properties',
					type: 'viewing'
				},
				madeAnOffer: {
					text: 'I\'ve made an offer',
					type: 'madeAnOffer'
				},
				offerAccepted: {
					text: 'I\'ve had an offer accepted',
					type: 'offerAccepted'
				},
			},
			thirdStageOptions: {
				findMortgage: {
					text: 'Find a mortgage',
					type: 'findMortgage'
				},
				calculateBorrowAmount: {
					text: 'See how much you could borrow',
					type: 'calculateBorrowAmount'
				},
			},
			selected: {
				firstStep: {},
				remortgage: {
					firstStep: {},
					secondStep: {},
				},
				secondStep: {},
				thirdStep: {},
			},
			remortgageSelected: {
				firstStep: {},
				secondStep: {},
			},
			firstStepSelected: {},
			progressionText: this.microcopy?.nextButtonLabel,
			options: {
				firstProperty: {
					text: '',
					icon: '/assets/images/houseIcon.svg',
					type: 'firstProperty',
				},
				remortgage: {
					text: '',
					icon: '/assets/images/remortgageIcon.svg',
					type: 'remortgage',
				},
				moveHome: {
					text: '',
					icon: '/assets/images/movingIcon.svg',
					type: 'moveHome',
				},
				buyToLet: {
					text: '',
					icon: '/assets/images/buyToLetIcon.svg',
					type: 'buyToLet',
				},
			},
		};
	},
	computed: {
		...mapStores(useMortgageStore),
		...mapState(useMortgageStore, ['mortgageType', 'propertySearchStage', 'howWesleyanCanHelp', 'remortgage']),
		nextPage() {
			if (this.selected?.thirdStep?.type === 'calculateBorrowAmount') {
				return this.microcopy?.mortgageCalculatorPageUrl;
			}
			else if (this.selected?.remortgage?.firstStep?.selected) {
				return 'current-mortgage';
			}
			return 'property-details';
		},
		planningToRemortgage() {
			return this.selected.remortgage.firstStep.selected;
		},
		thirdStageToBeShown() {
			return ['researching', 'viewing'].includes(this.selected.secondStep.type) && this.selected.secondStep.selected;
		},
		allButtonsValid() {
			if (this.planningToRemortgage && this.selected.remortgage.firstStep.selected) {
				return this.selected.remortgage.secondStep.selected;
			}
			else if (this.thirdStageToBeShown && this.selected.secondStep.selected) {
				return this.selected.thirdStep.selected;
			}
			else if (this.selected.firstStep.selected && this.selected.secondStep.selected) {
				return true;
			}
			return false;
		},
		continueButtonToBeShown() {
			return (this.selected.secondStep.selected && this.selected.firstStep.selected) || this.planningToRemortgage;
		}
	},

	methods: {
		...mapActions(useMortgageStore, ['postMortgageType', 'postPropertySearchStage', 'postHowWesleyanCanHelp', 'postRemortgage']),
		handleClick() {
			if (this.selected?.thirdStep?.type === 'calculateBorrowAmount') {
				window.location.href = this.nextPage;
			} else {
				scrollTo({ top: 0, behavior: 'smooth' });
				router.push(this.nextPage);
			}
		},
		handleBackButton() {
			router.back();
		},
		scrollToNextQuestion(nextQuestionID) {
			this.$nextTick(() => {
				let nextQuestion = document.getElementById(nextQuestionID);
				if (!nextQuestion && nextQuestionID !== 'continue-button') {
					nextQuestion = document.getElementById('continue-button');
				}
				if (!nextQuestion) {
					return;
				}
				nextQuestion.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				});
			});
		},
		setFirstStep(payload) {
			this.postMortgageType(payload);
			if (payload?.type?.includes('remortgage')) {
				this.remortgageSelected.firstStep = payload;
				this.selected.remortgage.firstStep = payload;
				this.selected.firstStep && (this.selected.firstStep.selected = false);

				if (payload.selected) {
					this.scrollToNextQuestion('remortgage-container');
				}
			}
			else {
				this.selected.firstStep = payload;
				this.selected.remortgage.firstStep.selected = false;

				if (payload.selected) {
					this.scrollToNextQuestion('property-stage-container');
				}
			}
		},
		getFirstStep(option) {
			if (option?.type?.includes('remortgage')) {
				return this.remortgageSelected.firstStep;
			}
			else {
				return this.selected.firstStep;
			}
		},
		setSecondStep(payload) {
			this.postRemortgage(payload);

			if (['remortgage', 'liveIn', 'rentOut'].includes(payload.type)) {
				this.remortgageSelected.secondStep = payload;
				this.selected.remortgage.secondStep = payload;
			}
			else {
				this.selected.secondStep = payload;
			}

			if (payload.selected) {
				this.scrollToNextQuestion('continue-button');
			}
		},
		getSecondStep(option) {
			if (['remortgage', 'liveIn', 'rentOut'].includes(option.type)) {
				return this.remortgageSelected.secondStep;
			}
			else {
				return this.selected.secondStep;
			}
		},
		initialiseMicrocopy() {
			this.options.firstProperty.text = this.microcopy?.firstPropertyLabel;
			this.options.remortgage.text = this.microcopy?.remortgageLabel;
			this.options.moveHome.text = this.microcopy?.movingHomeLabel;
			this.options.buyToLet.text = this.microcopy?.buyToLetLabel;
		}
	},
	mounted() {
		this.setFirstStep(this.mortgageType);
		this.setSecondStep(this.propertySearchStage);
		this.microcopy = window.mortgageRequestMicrocopy?.mortgageDetails;
		this.initialiseMicrocopy();
		document.title = this.microcopy?.metaTitle
	}
};

</script>
